import type { CellType, CellValues, TableRow, TableValue } from "./DataTable";

const typeMismatch = (name: string, actual: CellType, expected: readonly CellType[]) =>
  new Error(`Cell "${name}" is ${actual}, not ${expected.join(" or ")}`);

export function cellValue<IDType, K extends CellType>(row: TableRow<IDType>, name: string, ...types: [K, ...K[]]): CellValues[K] | undefined {
  const cell = row.values.find(v => v.name === name);
  if (!cell) return undefined;
  if (!(types as CellType[]).includes(cell.type)) throw typeMismatch(name, cell.type, types);
  return cell.value as CellValues[K];
}

export type RequiredCells = Record<string, readonly [name: string, type: CellType]>;
export type RequiredValues<S extends RequiredCells> = { [F in keyof S]: NonNullable<CellValues[S[F][1]]> };

/** Reads the cells a save cannot go without, keyed by the DTO field each one fills. */
export function requiredCellValues<IDType, const S extends RequiredCells>(row: TableRow<IDType>, cells: S):
  { ok: true; values: RequiredValues<S> } | { ok: false; missing: string[] } {
  const values: Record<string, unknown> = {};
  const missing: string[] = [];
  for (const [field, [name, type]] of Object.entries(cells)) {
    const value = cellValue(row, name, type);
    if (value == null) missing.push(name);
    else values[field] = value;
  }
  return missing.length ? { ok: false, missing } : { ok: true, values: values as RequiredValues<S> };
}

export function withCellValue<IDType, K extends CellType>(row: TableRow<IDType>, name: string, type: K, value: CellValues[K]): TableRow<IDType> {
  return {
    ...row,
    values: row.values.map(cell => {
      if (cell.name !== name) return cell;
      if (cell.type !== type) throw typeMismatch(name, cell.type, [type]);
      return { ...cell, value } as TableValue<IDType>;
    }),
  };
}
